/* eslint-disable no-case-declarations */
/* eslint-disable @next/next/no-img-element */
import Chevron from 'components/Chevron';
import BoxWithLightGreyFill from 'components/common/Box/BoxWithLightGreyFill';
import { EntryMethod } from 'components/enter/EntryMethod';
import EntryStack from 'components/enter/EntryStack';
import Separator from 'components/Separator';
import ToggleButton from 'components/ToggleButton';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { singularOrPlural } from 'utils/common';
import { ASSET_PREFIX, colors, PaymentSchedule } from 'utils/constants';
import { getNumberOfTreesPlanted } from '../helpers/getNumberOfTreesPlanted';
import { TotalCost } from '../with-one-time-purchases/TotalCost';
import { EditMyEntries } from './EditMyEntries';
import { PromoCodeEntry } from './PromoCodeEntry';

interface IOrderSummaryProps {
  desktopBreakpoint?: string;
  hideEditEntriesButton?: boolean;
}

const OrderSummary = observer(
  ({
    desktopBreakpoint = '1000px',
    hideEditEntriesButton = false,
  }: IOrderSummaryProps) => {
    const {
      days,
      dayEnabled,
      lines,
      entryMethod,
      paymentSchedule,
      boosted,
      planCode,
    } = useEnterStore();

    // This state is only used on mobile. On desktop, it's always expanded.
    const [expanded, setExpanded] = useState(false);
    const [editingEntries, setEditingEntries] = useState(false);

    // if (pageState === PageState.AUTHENTICATION) {
    //   return null;
    // }

    const numTreesPlantedPerWeek = getNumberOfTreesPlanted(
      entryMethod,
      dayEnabled.TUESDAY,
      dayEnabled.FRIDAY
    );
    const drawsPerWeek = days.length;
    const paymentFrequencyInWeeks = 1;
    const bulkPurchaseDuration = undefined;

    const setTuesdayDrawEnabled = () => {};

    const setFridayDrawEnabled = () => {};

    const shouldHideDrawDaysSelectorInFlow = false;
    const commonContent = (
      <div className="commonContentContainer grid gap-2em">
        <div className="grid  gap-1em text-centered">
          <p className="medium">
            Your numbers look great!
            <br />
            Here they are once more
          </p>

          {lines.map((line, i) => (
            <EntryStack
              key={i}
              stackCount={1}
              entry={line}
              style={{
                marginTop: i === 0 ? 0 : '-15%',
                zIndex: 0 - i,
              }}
              onClick={undefined}
              code={planCode}
            />
          ))}

          {!hideEditEntriesButton && (
            <div
              onClick={() => {
                setEditingEntries(true);
              }}
            >
              <p className="medium underline clickable bold">Edit my entries</p>
            </div>
          )}
        </div>

        <Separator
          // @ts-ignore
          margin={0}
        />

        <div className="flex flex-col">
          <div className="flex flex-col mx-auto ">
            <p className="medium">
              <img
                src={`${ASSET_PREFIX}/images/ticket.svg`}
                className="orderSummaryIcon"
                alt=""
              />
              {singularOrPlural(
                lines.length,
                '1 entry ',
                `${lines.length} entries `
              )}
              per draw
            </p>

            <p className="medium">
              <img
                src={`${ASSET_PREFIX}/images/enter/calendar-icon.svg`}
                className="orderSummaryIcon"
                alt=""
              />
              {singularOrPlural(
                drawsPerWeek * paymentFrequencyInWeeks,
                '1 draw',
                `${drawsPerWeek * paymentFrequencyInWeeks} draws`
              )}
              {entryMethod !== EntryMethod.OneOff &&
                ` per ${
                  paymentSchedule === PaymentSchedule.MONTHLY ? 'month' : 'week'
                }`}
              {/* {bulkPurchaseDuration && bulkPurchaseDuration > 1 && (
                <span className="bold">
                  &nbsp;for {bulkPurchaseDuration} weeks
                </span>
              )} */}
            </p>

            <p
              className="medium summaryPoint"
              style={{
                opacity: numTreesPlantedPerWeek === 0 ? 0.5 : 1.0,
              }}
            >
              <img
                src={`${ASSET_PREFIX}/images/common/trees-planted-solid-icon.svg`}
                className="orderSummaryIcon"
                alt=""
              />
              {singularOrPlural(
                numTreesPlantedPerWeek *
                  paymentFrequencyInWeeks *
                  (bulkPurchaseDuration || 1),
                '1 tree',
                `${
                  numTreesPlantedPerWeek *
                  paymentFrequencyInWeeks *
                  (bulkPurchaseDuration || 1)
                } trees`
              )}{' '}
              planted
              {entryMethod !== EntryMethod.OneOff &&
                ` per ${
                  paymentSchedule === PaymentSchedule.MONTHLY ? 'month' : 'week'
                }`}
              {bulkPurchaseDuration &&
                bulkPurchaseDuration > 1 &&
                ` (${numTreesPlantedPerWeek} per week)`}
            </p>

            {boosted && (
              <p className="medium">
                <img
                  src={`${ASSET_PREFIX}/images/enter/rocket.png`}
                  className="orderSummaryIcon"
                  alt=""
                />
                Boosted jackpot
              </p>
            )}
          </div>
        </div>
        <Separator
          // @ts-ignore
          margin={0}
        />
        <PromoCodeEntry />

        <TotalCost />

        <p className="medium bold text-center">👋 Cancel any time 👋</p>

        {expanded && (
          <div className="text-centered">
            <ToggleButton
              chevronReversed
              toggleHandler={() => setExpanded(false)}
              additionalClassName="fullWidth flexCentered flexRow uppercase"
              ignoreDefaultStyling
            >
              <p className="medium xbold">Collapse</p>
            </ToggleButton>
          </div>
        )}

        <style jsx>
          {`
            .commonContentContainer {
              isolation: isolate;
            }
          `}
        </style>
      </div>
    );

    return (
      <div className="orderSummary fullWidth">
        <BoxWithLightGreyFill additionalClassName="desktopContent flexCentered vertical-padding-2em">
          <div className="grid gap-2em vertical-padding-1em">
            <p className="large xbold noMargin">
              <img
                src={`${ASSET_PREFIX}/images/enter/shopping-cart.svg`}
                className="orderSummaryIcon"
                alt=""
              />
              Your order
            </p>

            <Separator
              // @ts-ignore
              margin={0}
            />

            {editingEntries ? (
              <EditMyEntries setEditingEntries={setEditingEntries} />
            ) : (
              commonContent
            )}
          </div>
        </BoxWithLightGreyFill>

        <div
          className="mobile fullWidth"
          style={{
            background: colors.footerBackground,
            width: '100%',
            padding: '0 !important',
            marginTop: '2em',
            paddingTop: 2,
            paddingBottom: 2,
            borderRadius: '12px',
          }}
        >
          <div
            className="clickable fullWidth grid gap-1em vertical-margin-1em"
            onClick={() => setExpanded(!expanded)}
          >
            <div
              className="flexRow"
              style={{
                justifyContent: 'space-between',
                padding: '0 0.9em',
                minHeight: 50,
              }}
            >
              <div>
                <p className="large xbold" style={{ fontSize: 15 }}>
                  <img
                    src={`${ASSET_PREFIX}/images/enter/shopping-cart.svg`}
                    className="orderSummaryIcon"
                    alt=""
                  />
                  Your order
                </p>
              </div>

              <Chevron
                direction="down"
                reversed={expanded}
                style={{
                  color: colors.primaryBrandGray,
                  display: 'inline-flex',
                  marginLeft: '0.7em',
                  verticalAlign: 'middle',
                  fontSize: '0.95em',
                  marginBottom: 4,
                }}
                inline={undefined}
              />
            </div>
          </div>
          {expanded &&
            (editingEntries ? (
              <EditMyEntries setEditingEntries={setEditingEntries} />
            ) : (
              commonContent
            ))}
        </div>

        <style jsx>
          {`
            .container {
              width: 100%;
              display: flex;
              justify-content: center;
            }

            :global(.orderSummary .orderSummaryIcon) {
              width: 20px;
              display: inline-block;
              vertical-align: middle;
              margin-right: 0.8em;
            }

            .mobile {
              padding: 0 2em;
            }

            :global(.orderSummary strong) {
              font-weight: 800;
            }

            :global(.orderSummary .editMyEntriesButton) {
              text-decoration: underline;
              cursor: pointer;
            }

            @media (max-width: 400px) {
              .mobile {
                padding: 0 0.8em;
                margin: 2em 0;
              }
            }

            :global(.desktopContent) {
              width: 100%;
              max-width: 480px;
              margin: 2em 0;
              border-radius: 12px;
              text-align: center;
              display: none;
            }

            @media (min-width: ${desktopBreakpoint}) {
              .mobile {
                display: none;
              }
              :global(.desktopContent) {
                display: flex;
              }
            }
          `}
        </style>
      </div>
    );
  }
);

export default OrderSummary;
